import "./AppBar.scss";
import logo from "../../Images/logo.png";
import { Palette, Smartphone, CodeXml, Menu, X } from "lucide-react";
import BandieraItaliana from "../../Images/Flag/Flag_of_Italy.svg";
import BandieraInglese from "../../Images/Flag/Flag_of_the_United_Kingdom.svg";
import BandieraSpagnola from "../../Images/Flag/Flag_of_Spain.svg";
import { useTranslation } from "react-i18next";
import { BookText, ChevronDown } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import TOSPopup from "../../Components/TOS/TOS";
import { discordUrl } from "../../Global";

interface FlagContextType {
    image: string;
    name: string;
    language: "it" | "en" | "es";
}

interface navButtonContextType {
    name: string;
    to: string;
}

interface appbarProps {
    showOurWorks: boolean;
    numberFlag: number;
}

function AppBar({ showOurWorks, numberFlag }: appbarProps) {
    const [flagMenu, setFlagMenu] = useState<boolean>(false);
    const [thisFlag, setThisFlag] = useState<number>(numberFlag);
    const [servicesMenu, setServicesMenu] = useState<boolean>(false);
    const [tosOpen, setTosOpen] = useState<boolean>(false);

    // Mobile
    const [mobileMenu, setMobileMenu] = useState<boolean>(false);
    const [mobileServices, setMobileServices] = useState<boolean>(false);

    const { t, i18n } = useTranslation();

    const changeLanguage = (language: "it" | "en" | "es") => {
        i18n.changeLanguage(language);
        localStorage.setItem("language", language);
    };

    const flagList: FlagContextType[] = [
        { image: BandieraItaliana, name: "Italiano", language: "it" },
        { image: BandieraInglese, name: "English", language: "en" },
        { image: BandieraSpagnola, name: "Español", language: "es" },
    ];

    const languageRef = useRef<HTMLDivElement>(null);

    let navButton: navButtonContextType[] = [
        { name: t("appbar.navButton.home"), to: "/" },
        { name: t("appbar.navButton.Services.name"), to: "/servizi" },
        { name: t("appbar.navButton.FAQ"), to: "/faq" },
    ];

    if (showOurWorks) {
        navButton.splice(1, 0, {
            name: t("appbar.navButton.works"),
            to: "/lavori",
        });
    }

    // Click fuori dal menu lingua
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                languageRef.current &&
                !languageRef.current.contains(event.target as Node)
            ) {
                setFlagMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Blocca lo scroll della pagina quando il menu mobile è aperto
    useEffect(() => {
        document.body.style.overflow = mobileMenu ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileMenu]);

    // Chiude il menu mobile se si torna su desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 650) {
                setMobileMenu(false);
                setMobileServices(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <header className="appbar">
            <div className="appbar-container">
                <Link to="/" className="appbar__brand">
                    <div className="appbar__logo">
                        <img
                            src={logo}
                            alt="EnderClub logo"
                            className="appbar__logo__image"
                        />
                    </div>
                    <span className="appbar__name">
                        <span className="appbar__name__ender">Ender</span>
                        Club
                    </span>
                </Link>

                <nav className="appbar__nav">
                    {navButton.map((item) => {
                        if (item.name === t("appbar.navButton.Services.name")) {
                            return (
                                <div
                                    key={item.name}
                                    className={`appbar__services ${
                                        servicesMenu ? "appbar__services--open" : ""
                                    }`}
                                    onMouseEnter={() => setServicesMenu(true)}
                                    onMouseLeave={() => setServicesMenu(false)}
                                >
                                    <NavLink
                                        to={item.to}
                                        onClick={() => setServicesMenu(false)}
                                        className={({ isActive }) =>
                                            `appbar__link ${
                                                isActive ? "appbar__link--active" : ""
                                            }`
                                        }
                                    >
                                        <span>{item.name}</span>
                                        <ChevronDown
                                            className="appbar__services-arrow"
                                            size={14}
                                            strokeWidth={2.2}
                                        />
                                    </NavLink>

                                    <div className="appbar__services-menu">
                                        <Link
                                            onClick={() => setServicesMenu(false)}
                                            className="appbar__services-menu__link"
                                            to="/servizi/video"
                                        >
                                            <Smartphone />
                                            <div className="appbar__services-menu__item">
                                                <span className="appbar__services-menu-title">
                                                    {t("appbar.navButton.Services.videos.name")}
                                                </span>
                                                <span className="appbar__services-menu-description">
                                                    {t("appbar.navButton.Services.videos.description")}
                                                </span>
                                            </div>
                                        </Link>

                                        <Link
                                            onClick={() => setServicesMenu(false)}
                                            className="appbar__services-menu__link"
                                            to="/servizi/grafica"
                                        >
                                            <Palette />
                                            <div className="appbar__services-menu__item">
                                                <span className="appbar__services-menu-title">
                                                    {t("appbar.navButton.Services.graphic.name")}
                                                </span>
                                                <span className="appbar__services-menu-description">
                                                    {t("appbar.navButton.Services.graphic.description")}
                                                </span>
                                            </div>
                                        </Link>

                                        <Link
                                            onClick={() => setServicesMenu(false)}
                                            className="appbar__services-menu__link"
                                            to="/servizi/development"
                                        >
                                            <CodeXml />
                                            <div className="appbar__services-menu__item">
                                                <span className="appbar__services-menu-title">
                                                    {t("appbar.navButton.Services.development.name")}
                                                </span>
                                                <span className="appbar__services-menu-description">
                                                    {t("appbar.navButton.Services.development.description")}
                                                </span>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <NavLink
                                key={item.name}
                                to={item.to}
                                className={({ isActive }) =>
                                    `appbar__link ${
                                        isActive ? "appbar__link--active" : ""
                                    }`
                                }
                            >
                                {item.name}
                            </NavLink>
                        );
                    })}

                    <button
                        onClick={() => setTosOpen(true)}
                        className="appbar__tos"
                    >
                        <BookText className="appbar__tos-icon" size={13} />
                        <span>TOS</span>
                    </button>
                </nav>

                <TOSPopup open={tosOpen} onClose={() => setTosOpen(false)} />

                <div className="appbar__actions">
                    <div className="appbar__language-wrapper">
                        <button
                            type="button"
                            className="appbar__language"
                            onClick={() => setFlagMenu((prev) => !prev)}
                            aria-label="Seleziona lingua"
                            aria-expanded={flagMenu}
                        >
                            <img
                                src={flagList[thisFlag].image}
                                alt={flagList[thisFlag].name}
                                className="appbar__flag-image"
                            />
                        </button>

                        {flagMenu && (
                            <div
                                ref={languageRef}
                                className="appbar__flag-container"
                            >
                                {flagList.map((flag, index) => (
                                    <button
                                        type="button"
                                        key={flag.name}
                                        className={`appbar__flag ${
                                            index === thisFlag
                                                ? "appbar__flag--active"
                                                : ""
                                        }`}
                                        onClick={() => {
                                            setFlagMenu(false);
                                            setThisFlag(index);
                                            changeLanguage(flag.language);
                                        }}
                                        disabled={i18n.language === flag.language}
                                    >
                                        <img src={flag.image} alt={flag.name} />
                                        <span>{flag.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <a
                        href={discordUrl}
                        className="appbar__contact"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Discord
                    </a>

                    <button
                        type="button"
                        className="appbar__hamburger"
                        onClick={() => setMobileMenu((v) => !v)}
                        aria-label={mobileMenu ? "Chiudi menu" : "Apri menu"}
                        aria-expanded={mobileMenu}
                        aria-controls="appbar-mobile-menu"
                    >
                        {mobileMenu ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* ==============================
                MOBILE MENU
            ============================== */}
            {mobileMenu && (
                <div
                    id="appbar-mobile-menu"
                    className="appbar__mobile-menu"
                >
                    <nav className="appbar__mobile-nav">
                        {navButton.map((item) => {
                            if (
                                item.name ===
                                t("appbar.navButton.Services.name")
                            ) {
                                return (
                                    <div
                                        key={item.name}
                                        className="appbar__mobile-services"
                                    >
                                        <Link
                                            to="/servizi/video"
                                            className="appbar__mobile-sublink"
                                            onClick={() =>
                                                setMobileServices((v) => !v)
                                            }
                                        >
                                            <span>{item.name}</span>
                                            <ChevronDown
                                                size={16}
                                                className={
                                                    mobileServices
                                                        ? "appbar__chevron--open"
                                                        : ""
                                                }
                                            />
                                        </Link>

                                        {mobileServices && (
                                            <div className="appbar__mobile-submenu">
                                                <Link
                                                    to="/servizi/video"
                                                    className="appbar__mobile-sublink"
                                                    onClick={() => {
                                                        setMobileMenu(false);
                                                        setMobileServices(false);
                                                    }}
                                                >
                                                    <Smartphone size={16} />
                                                    <span>
                                                        {t(
                                                            "appbar.navButton.Services.videos.name"
                                                        )}
                                                    </span>
                                                </Link>

                                                <Link
                                                    to="/servizi/grafica"
                                                    className="appbar__mobile-sublink"
                                                    onClick={() => {
                                                        setMobileMenu(false);
                                                        setMobileServices(false);
                                                    }}
                                                >
                                                    <Palette size={16} />
                                                    <span>
                                                        {t(
                                                            "appbar.navButton.Services.graphic.name"
                                                        )}
                                                    </span>
                                                </Link>

                                                <Link
                                                    to="/servizi/development"
                                                    className="appbar__mobile-sublink"
                                                    onClick={() => {
                                                        setMobileMenu(false);
                                                        setMobileServices(false);
                                                    }}
                                                >
                                                    <CodeXml size={16} />
                                                    <span>
                                                        {t(
                                                            "appbar.navButton.Services.development.name"
                                                        )}
                                                    </span>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <NavLink
                                    key={item.name}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `appbar__mobile-link ${
                                            isActive
                                                ? "appbar__mobile-link--active"
                                                : ""
                                        }`
                                    }
                                    onClick={() => setMobileMenu(false)}
                                >
                                    {item.name}
                                </NavLink>
                            );
                        })}

                        <button
                            type="button"
                            className="appbar__mobile-link appbar__mobile-tos"
                            onClick={() => {
                                setTosOpen(true);
                                setMobileMenu(false);
                            }}
                        >
                            <BookText size={16} />
                            <span>TOS</span>
                        </button>

                        <a
                            href={discordUrl}
                            className="appbar__mobile-discord"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileMenu(false)}
                        >
                            Discord
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}

export default AppBar;